/* =====================================================================
 * Skill Store — 사용자 스킬 전역 상태 관리
 *
 * 업로드한 스킬 목록, 활성 스킬 선택, 모달 상태를 관리한다.
 * ===================================================================== */

import { create } from 'zustand';
import type { SharedSkillSummary, SkillSummary } from '@auw/api-client';
import {
  listSkills,
  uploadSkill,
  deleteSkill as deleteSkillApi,
  renameSkill as renameSkillApi,
  getSkillFavorites,
  setSkillFavorites,
  listSharedSkills,
  shareSkill as shareSkillApi,
  unshareSkill as unshareSkillApi,
} from '@auw/api-client';

interface SkillState {
  skills: SkillSummary[];
  activeSkillIds: string[];
  isModalOpen: boolean;
  modalInitialTab: 'my' | 'ref' | 'guide';
  isLoading: boolean;
  error: string | null;
  favoriteRefIds: string[];
  /* 사내 공유 스킬 (마켓플레이스 "공유 스킬" 탭). 내 스킬 목록과 별도 수명. */
  sharedSkills: SharedSkillSummary[];
  isSharedLoading: boolean;
  sharedError: string | null;

  openModal: (tab?: 'my' | 'ref') => void;
  closeModal: () => void;

  fetchSkills: () => Promise<void>;
  uploadSkills: (files: File[]) => Promise<SkillSummary[]>;
  deleteSkill: (skillId: string) => Promise<void>;
  renameSkill: (skillId: string, newName: string) => Promise<void>;

  toggleSkill: (skillId: string) => void;
  setActiveSkillIds: (ids: string[]) => void;
  clearActiveSkills: () => void;

  fetchFavorites: () => Promise<void>;
  toggleFavorite: (refSkillId: string) => Promise<void>;

  fetchSharedSkills: () => Promise<void>;
  /** 내 스킬을 사내에 공유. 실패하면 false 를 돌려주고 `error` 에 사유를 남긴다. */
  shareSkill: (skillId: string) => Promise<boolean>;
  unshareSkill: (skillId: string) => Promise<boolean>;
}

export const useSkillStore = create<SkillState>((set, get) => ({
  skills: [],
  activeSkillIds: [],
  isModalOpen: false,
  modalInitialTab: 'my',
  isLoading: false,
  error: null,
  favoriteRefIds: [],
  sharedSkills: [],
  isSharedLoading: false,
  sharedError: null,

  openModal: (tab: 'my' | 'ref' | 'guide' = 'my') => set({ isModalOpen: true, modalInitialTab: tab }),
  closeModal: () => set({ isModalOpen: false }),

  fetchSkills: async () => {
    set({ isLoading: true, error: null });
    try {
      const result = await listSkills();
      set({ skills: result.skills, isLoading: false });
    } catch (err) {
      set({ error: String(err), isLoading: false });
    }
  },

  uploadSkills: async (files: File[]) => {
    set({ isLoading: true, error: null });
    try {
      const result = await uploadSkill(files);
      await get().fetchSkills();
      set({
        isLoading: false,
        error: [result.errors?.join('\n'), get().error].filter(Boolean).join('\n') || null,
      });

      return result.skills;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : String(err), isLoading: false });
      return [];
    }
  },

  deleteSkill: async (skillId: string) => {
    try {
      await deleteSkillApi(skillId);
      set((s) => ({
        skills: s.skills.filter((sk) => sk.skill_id !== skillId),
        activeSkillIds: s.activeSkillIds.filter((id) => id !== skillId),
      }));
    } catch (err) {
      set({ error: String(err) });
    }
  },

  renameSkill: async (skillId: string, newName: string) => {
    try {
      await renameSkillApi(skillId, newName);
      set((s) => ({
        skills: s.skills.map((sk) =>
          sk.skill_id === skillId ? { ...sk, name: newName } : sk,
        ),
      }));
    } catch (err) {
      set({ error: String(err) });
    }
  },

  toggleSkill: (skillId: string) =>
    set((s) => ({
      activeSkillIds: s.activeSkillIds.includes(skillId) ? [] : [skillId],
    })),

  setActiveSkillIds: (ids: string[]) => set({ activeSkillIds: ids }),
  clearActiveSkills: () => set({ activeSkillIds: [] }),

  fetchFavorites: async () => {
    try {
      const ids = await getSkillFavorites();
      set({ favoriteRefIds: ids });
    } catch {
      /* silent */
    }
  },

  toggleFavorite: async (refSkillId: string) => {
    const prev = get().favoriteRefIds;
    const next = prev.includes(refSkillId)
      ? prev.filter((id) => id !== refSkillId)
      : [...prev, refSkillId];
    set({ favoriteRefIds: next });
    try {
      await setSkillFavorites(next);
    } catch {
      set({ favoriteRefIds: prev });
    }
  },

  fetchSharedSkills: async () => {
    set({ isSharedLoading: true, sharedError: null });
    try {
      const result = await listSharedSkills();
      set({ sharedSkills: result.skills, isSharedLoading: false });
    } catch (err) {
      set({ sharedError: err instanceof Error ? err.message : String(err), isSharedLoading: false });
    }
  },

  shareSkill: (skillId: string) => setSkillShared(skillId, true),
  unshareSkill: (skillId: string) => setSkillShared(skillId, false),
}));

/* 공유 / 공유 취소는 서버 확정 후에만 카드를 갱신한다 (낙관적 갱신 없음) —
 * 공유 상태는 다른 사용자에게 바로 노출되는 값이라 실패 시 되돌릴 표면이
 * 두 군데(내 스킬·공유 스킬)라서다. 성공하면 공유 목록도 다시 받는다. */
async function setSkillShared(skillId: string, shared: boolean): Promise<boolean> {
  const { setState, getState } = useSkillStore;
  try {
    const result = shared ? await shareSkillApi(skillId) : await unshareSkillApi(skillId);
    setState((s) => ({
      skills: s.skills.map((sk) =>
        sk.skill_id === skillId
          ? { ...sk, is_shared: result.is_shared, shared_at: result.shared_at }
          : sk,
      ),
    }));
    await getState().fetchSharedSkills();
    return true;
  } catch (err) {
    setState({ error: err instanceof Error ? err.message : String(err) });
    return false;
  }
}
